import React, { useEffect, useState } from 'react'
import { Box, Button, Grid } from '@mui/material'
import { AxiosError } from 'axios'
import { array, object, string } from 'yup'

import { ToggleGroup } from '@/components'

import { Form, FormSchema, FormSchemaMessages, FormSchemaTests, SubmitButton } from '../form'
import { Dialog } from '../dialog.tsx'
import { useDialog } from '../../hooks'
import { useSnackbarContext } from '../snackbar'
import { ApiErrorResponse, AutoReportDeleteBodyDto, AutoReportParams } from '../../types.ts'
import { useCreateAutoReportMutation, useDeleteAutoReportMutation, useGetSubscriptionAutoReports } from '../../services'

import { AddEmailsWindow } from './add-emails-window.tsx'
import { SubscriptionsList } from '@/components/auto-report-dialog/subscriptions-list.tsx'

export interface AutoReportRequestFormState extends Omit<AutoReportParams, 'emails'> {
  // до 3-x адресов в строку через запятую
  emails: Array<{ value: string }>
}

const AUTO_REPORT_FORM = 'autoReport'
const autoReportFormValidationSchema = object<FormSchema<AutoReportRequestFormState>>({
  emails: array().of(
    object({
      value: string().required(FormSchemaMessages.required).test(FormSchemaTests.isEmail),
    })
  ),
})

interface AutoReportDialogProps {
  kind: AutoReportParams['kind']
}

export const AutoReportDialog = ({ kind }: AutoReportDialogProps): React.ReactElement => {
  const { showDialog, handleShowDialog, handleHideDialog } = useDialog()
  const [toggleValue, setToggleValue] = useState<'create' | 'show'>('create')
  const [items, setItems] = useState<
    {
      id: number
      email: string
      isDelete: boolean
      format: string
      frequency: string
    }[]
  >([])
  const { mutateAsync: mutateAsyncSubscribe, reset: resetRequestSubscribe } = useCreateAutoReportMutation()
  const { mutateAsync: mutateAsyncUnsubscribe } = useDeleteAutoReportMutation()
  const { mutateAsync: mutateAsyncGetList } = useGetSubscriptionAutoReports()
  const { addSnack } = useSnackbarContext()

  const handleGetList = async () => {
    const response = await mutateAsyncGetList({ report_kind: kind })
    const data = response.data
    const itemsList = data.map((e) => ({
      id: e.id,
      email: e.email,
      isDelete: false,
      format: e.reportType,
      frequency: e.frequency,
    }))
    setItems(itemsList)
  }

  useEffect(() => {
    if (showDialog) {
      handleGetList()
    }
  }, [showDialog])

  const handleCloseAutoReportsDialog = () => {
    handleHideDialog()
    setItems([])
    setToggleValue('create')
  }

  const handleSubscribeSubmit = async (formValues: AutoReportRequestFormState) => {
    try {
      const response = await mutateAsyncSubscribe({
        ...formValues,
        emails: formValues.emails.map((email) => email.value),
      })

      const isSuccess = response.data.status === 'success'
      addSnack({
        message: response.data?.message ?? 'Произошла непредвиденная ошибка',
        severity: isSuccess ? 'success' : 'error',
        timeout: isSuccess ? 6000 : 3000,
        closeIcon: false,
      })
      if (isSuccess) {
        handleCloseAutoReportsDialog()
        resetRequestSubscribe()
      }
    } catch (axiosError) {
      const error = axiosError as AxiosError<ApiErrorResponse>
      const errorMessage = error.response?.data?.message ?? 'Произошла непредвиденная ошибка'

      addSnack({
        message: errorMessage,
        severity: 'error',
        timeout: 3000,
      })
    }
  }

  const handleUnsubscribeSubmit = async () => {
    try {
      const requestDto: AutoReportDeleteBodyDto['items'] = []
      const deletingItems = items.filter((e) => e.isDelete)

      for (const item of deletingItems) {
        const itemRequest = requestDto.find((e) => e.id === item.id)
        if (itemRequest) {
          itemRequest.emails.push(item.email)
        } else {
          requestDto.push({ id: item.id, emails: [item.email] })
        }
      }

      const response = await mutateAsyncUnsubscribe({ items: requestDto })

      const isSuccess = response.data.status === 'success'
      addSnack({
        message: response.data?.message ?? 'Произошла непредвиденная ошибка',
        severity: isSuccess ? 'success' : 'error',
        timeout: isSuccess ? 6000 : 3000,
        closeIcon: false,
      })
      if (isSuccess) {
        handleCloseAutoReportsDialog()
      }
    } catch (axiosError) {
      let errorMessage = 'Произошла непредвиденная ошибка'

      const error = axiosError as AxiosError<ApiErrorResponse>
      if (error.isAxiosError && error.response?.data?.errorDetails) {
        errorMessage = error.response.data.errorDetails
      }

      addSnack({
        message: errorMessage,
        severity: 'error',
        timeout: 3000,
      })
    }
  }

  const handleDeleteEmail = (id: number, email: string) => {
    const newItemsList = [...items]
    const item = newItemsList.find((e) => e.id === id && e.email === email)
    if (item) {
      item.isDelete = true
    }
    setItems(newItemsList)
  }

  const handleReturnEmail = (id: number, email: string) => {
    const newItemsList = [...items]
    const item = newItemsList.find((e) => e.id === id && e.email === email)
    if (item) {
      item.isDelete = false
    }
    setItems(newItemsList)
  }

  const isDisabledButtonOnUnsubscribe = items.every((e) => !e.isDelete)

  return (
    <Box display="flex" justifyContent="flex-end">
      <Button onClick={handleShowDialog} variant="outlined">
        Автоотчёт на email
      </Button>
      {showDialog && (
        <Form<AutoReportRequestFormState>
          name={AUTO_REPORT_FORM}
          validationSchema={autoReportFormValidationSchema}
          initialValues={{
            formatReport: 'xlsx',
            kind,
            emails: [{ value: '' }],
            monthly: false,
            daily: false,
          }}
          onSubmit={handleSubscribeSubmit}
        >
          <Dialog
            title="Заявка на автоотчёт"
            open={showDialog}
            onClose={handleCloseAutoReportsDialog}
            content={
              <Grid container flexDirection="column" gap={2}>
                <ToggleGroup
                  width="fill"
                  items={[
                    { title: 'Добавить email', value: 'create' },
                    { title: 'Все подписки', value: 'show' },
                  ]}
                  value={toggleValue}
                  onChange={(_, item) => setToggleValue(item)}
                />

                {toggleValue === 'show' ? (
                  <SubscriptionsList items={items} onDelete={handleDeleteEmail} onReturn={handleReturnEmail} />
                ) : (
                  <AddEmailsWindow />
                )}
              </Grid>
            }
            actions={
              toggleValue === 'show' ? (
                <Button fullWidth disabled={isDisabledButtonOnUnsubscribe} onClick={handleUnsubscribeSubmit}>
                  Сохранить
                </Button>
              ) : (
                <SubmitButton form={AUTO_REPORT_FORM}>Отправить заявку</SubmitButton>
              )
            }
          />
        </Form>
      )}
    </Box>
  )
}
